import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import type { ProgramData, CourseData, PackageData, ProgramCardProps } from './types';
import './ProgramCard.css';

// Configure DOMPurify with strict allowlist
const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'strong', 'em', 'ul', 'ol', 'li', 'div'],
    ALLOWED_ATTR: [],
  });
};

// ─── Icons ─────────────────────────────────────────────────────

const BookIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const ClipboardIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);

const BriefcaseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);

const BoltIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const GraduationCapIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c3 3 9 3 12 0v-5" />
  </svg>
);

const PackageIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ─── Utility Functions ─────────────────────────────────────────

const formatPrice = (price: number, currency: string, lang: string): string => {
  const locale = lang === 'en' ? 'en-GB' : 'it-IT';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

const calculateDiscount = (original: number, current: number): number => {
  return Math.round(((original - current) / original) * 100);
};

const getCity = (location: string): string => location.split(',')[0].trim();

const isCourse = (program: ProgramData): program is CourseData => program.type === 'CourseData';
const isPackage = (program: ProgramData): program is PackageData => program.type === 'PackageData';

// Country flag mapping from location string
const getCountryFlag = (location: string): string => {
  const locationLower = location.toLowerCase();

  // UK variations
  if (locationLower.includes('uk') || locationLower.includes('united kingdom') ||
      locationLower.includes('england') || locationLower.includes('scotland') ||
      locationLower.includes('wales')) return '🇬🇧';

  // Ireland
  if (locationLower.includes('ireland') || locationLower.includes('dublin')) return '🇮🇪';

  // USA
  if (locationLower.includes('usa') || locationLower.includes('united states') ||
      locationLower.includes('america')) return '🇺🇸';

  // Canada
  if (locationLower.includes('canada')) return '🇨🇦';

  // Australia
  if (locationLower.includes('australia')) return '🇦🇺';

  // New Zealand
  if (locationLower.includes('new zealand')) return '🇳🇿';

  // Malta
  if (locationLower.includes('malta')) return '🇲🇹';

  // Spain
  if (locationLower.includes('spain') || locationLower.includes('españa')) return '🇪🇸';

  // France
  if (locationLower.includes('france')) return '🇫🇷';

  // Germany
  if (locationLower.includes('germany') || locationLower.includes('deutschland')) return '🇩🇪';

  // Italy
  if (locationLower.includes('italy') || locationLower.includes('italia')) return '🇮🇹';

  return '';
};

// Category icon and color mapping
type CategoryInfo = {
  icon: React.FC;
  color: string;
};

const getCategoryStyle = (category: string | undefined): CategoryInfo => {
  if (!category) {
    return { icon: GraduationCapIcon, color: 'blue' };
  }

  const categoryLower = category.toLowerCase();

  if (categoryLower.includes('exam') || categoryLower.includes('ielts') ||
      categoryLower.includes('toefl') || categoryLower.includes('cambridge') ||
      categoryLower.includes('preparation')) {
    return { icon: ClipboardIcon, color: 'purple' };
  }

  if (categoryLower.includes('business') || categoryLower.includes('professional') ||
      categoryLower.includes('executive')) {
    return { icon: BriefcaseIcon, color: 'amber' };
  }

  if (categoryLower.includes('intensive')) {
    return { icon: BoltIcon, color: 'orange' };
  }

  if (categoryLower.includes('general')) {
    return { icon: BookIcon, color: 'blue' };
  }

  return { icon: GraduationCapIcon, color: 'blue' };
};

const formatStartDate = (dateStr: string | undefined, lang: string): string | null => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    const locale = lang === 'en' ? 'en-GB' : 'it-IT';
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
};

// Format SCREAMING_SNAKE_CASE to Title Case (fallback when no translation)
const formatSnakeCase = (code: string): string => {
  return code
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Translate duration string (e.g., "4 WEEK" -> "4 settimane")
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translateDuration = (duration: string, t: any): string => {
  const match = duration.match(/^(\d+)\s+(.+)$/);
  if (!match) return duration;

  const [, amount, unit] = match;
  // Handle pluralization: use plural key if amount != 1
  const unitKey = amount === '1' ? unit : `${unit}S`;
  const translated = t(`programCard.durations.${unitKey}`, { defaultValue: formatSnakeCase(unit) });
  return `${amount} ${translated}`;
};

// Translate category (e.g., "EXAM_PREPARATION" -> "Preparazione Esami")
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translateCategory = (category: string | undefined, t: any, fallback: string): string => {
  if (!category) return fallback;
  return t(`programCard.categories.${category}`, { defaultValue: formatSnakeCase(category) });
};

// Translate English level (e.g., "UPPER_INTERMEDIATE" -> "Intermedio Superiore")
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translateLevel = (level: string | undefined, t: any): string | undefined => {
  if (!level) return undefined;
  return t(`programCard.levels.${level}`, { defaultValue: formatSnakeCase(level) });
};

// ─── Main Component ────────────────────────────────────────────

export const ProgramCard: React.FC<ProgramCardProps> = ({ data, portalContainer }) => {
  const { t } = useTranslation();
  const programs = Array.isArray(data) ? data : [data];
  const [selectedProgram, setSelectedProgram] = useState<ProgramData | null>(null);

  return (
    <div className="card-section-wrapper">
      <div className="card-section-header">
        <div className="card-section-title-row">
          <h2 className="card-section-title">{t('programCard.sectionTitle')}</h2>
          <span className="card-section-count">{programs.length}</span>
        </div>
      </div>
      <div className="card-grid-2col">
        {programs.map((program, index) => (
          isCourse(program)
            ? <CourseCardItem
                key={program.programId || index}
                course={program}
                index={index}
                onSelect={() => setSelectedProgram(program)}
              />
            : isPackage(program)
              ? <PackageCardItem
                  key={program.programId || index}
                  pkg={program}
                  index={index}
                  onSelect={() => setSelectedProgram(program)}
                />
              : null
        ))}
      </div>
      <p className="card-section-disclaimer">{t('programCard.priceDisclaimer')}</p>

      {selectedProgram && (
        portalContainer
          ? createPortal(
              <ProgramDetailModal
                program={selectedProgram}
                onClose={() => setSelectedProgram(null)}
              />,
              portalContainer
            )
          : <ProgramDetailModal
              program={selectedProgram}
              onClose={() => setSelectedProgram(null)}
            />
      )}
    </div>
  );
};

// ─── Icons for Specs ────────────────────────────────────────────

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const DurationIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const IntensityIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const LevelIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 20h4v-8H2v8zM9 20h4v-16H9v16zM16 20h4v-12h-4v12z" />
  </svg>
);

// ─── Course Card ───────────────────────────────────────────────

interface CourseCardItemProps {
  course: CourseData;
  index: number;
  onSelect: () => void;
}

const CourseCardItem: React.FC<CourseCardItemProps> = ({ course, index, onSelect }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [logoError, setLogoError] = useState(false);
  // Use priceEur if available, otherwise fallback to price (for mock data compatibility)
  const displayPrice = course.priceEur ?? course.price;
  const displayCurrency = course.priceEur ? 'EUR' : course.currency;
  const displayOriginalPrice = course.originalPriceEur ?? course.originalPrice;
  const hasPromo = course.hasPromotion && displayOriginalPrice;
  const discount = hasPromo ? calculateDiscount(displayOriginalPrice!, displayPrice) : 0;
  const categoryStyle = getCategoryStyle(course.courseCategory);
  const CategoryIcon = categoryStyle.icon;
  const categoryLabel = translateCategory(course.courseCategory, t, t('programCard.fallbackCategory'));
  const flag = getCountryFlag(course.location);
  const startDate = formatStartDate(course.startDate, lang);
  const translatedDuration = translateDuration(course.duration, t);
  const perWeek = course.durationWeeks && course.durationWeeks > 0
    ? formatPrice(Math.round(displayPrice / course.durationWeeks), displayCurrency, lang)
    : null;
  const showOriginalCurrency = course.priceEur && course.currency !== 'EUR';

  return (
    <article
      className={`program-card program-card--course ${hasPromo ? 'has-promotion' : ''}`}
      style={{ animationDelay: `${index * 0.05}s` }}
      onClick={onSelect}
    >
      {/* Logo spanning full height */}
      <div className="program-logo">
        {course.photoUrl && !logoError ? (
          <img
            src={course.photoUrl}
            alt={course.schoolName}
            onError={() => setLogoError(true)}
          />
        ) : (
          <div className="program-logo-placeholder">
            <GraduationCapIcon />
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="program-main">
        {/* Zone 1: Type & Offers */}
        <div className="program-zone-type">
          <span className={`category-badge category-badge--${categoryStyle.color}`}>
            <CategoryIcon />
            <span>{categoryLabel}</span>
          </span>
          {hasPromo && (
            <span className="promo-badge">
              <SparkleIcon />
              <span>-{discount}%</span>
            </span>
          )}
        </div>

        {/* Zone 2: Identity */}
        <div className="program-zone-identity">
          <h3 className="program-name">{course.name}</h3>
          <span className="program-school">{course.schoolName}</span>
        </div>

        {/* Zone 3: Quick Specs Grid */}
        <div className="program-zone-specs">
          <div className="spec-item">
            <MapPinIcon />
            <span>{flag} {getCity(course.location)}</span>
          </div>
          <div className="spec-item">
            <DurationIcon />
            <span>{translatedDuration}</span>
          </div>
          {startDate && (
            <div className="spec-item">
              <CalendarIcon />
              <span>{startDate}</span>
            </div>
          )}
          {course.hoursPerWeek && (
            <div className="spec-item">
              <IntensityIcon />
              <span>{Math.round(course.hoursPerWeek)}h/{t('programCard.perWeek')}</span>
            </div>
          )}
          {course.ageRequirement && (
            <div className="spec-item">
              <UserIcon />
              <span>{course.ageRequirement}</span>
            </div>
          )}
          {course.requiredEnglishLevel && (
            <div className="spec-item">
              <LevelIcon />
              <span>{t('programCard.minLevel')}: {translateLevel(course.requiredEnglishLevel, t)}</span>
            </div>
          )}
        </div>

        {/* Zone 4: Price */}
        <div className="program-zone-price">
          <div className="program-prices">
            {hasPromo && (
              <span className="program-price-old">
                {formatPrice(displayOriginalPrice!, displayCurrency, lang)}
              </span>
            )}
            <span className={`program-price ${hasPromo ? 'program-price--promo' : ''}`}>
              {formatPrice(displayPrice, displayCurrency, lang)}<span className="price-asterisk">*</span>
            </span>
          </div>
          {perWeek && (
            <span className="program-price-weekly">{perWeek}/{t('programCard.perWeek')}</span>
          )}
          {showOriginalCurrency && (
            <span className="program-price-original">
              (~{formatPrice(course.price, course.currency, lang)})
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

// ─── Package Card ──────────────────────────────────────────────

interface PackageCardItemProps {
  pkg: PackageData;
  index: number;
  onSelect: () => void;
}

const PackageCardItem: React.FC<PackageCardItemProps> = ({ pkg, index, onSelect }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [logoError, setLogoError] = useState(false);
  const flag = getCountryFlag(pkg.location);
  const startDate = formatStartDate(pkg.startDate, lang);
  // Use priceEur if available, otherwise fallback to price (for mock data compatibility)
  const displayPrice = pkg.priceEur ?? pkg.price;
  const displayCurrency = pkg.priceEur ? 'EUR' : pkg.currency;
  const displayOriginalPrice = pkg.originalPriceEur ?? pkg.originalPrice;
  const hasPromo = pkg.hasPromotion && displayOriginalPrice;
  const discount = hasPromo ? calculateDiscount(displayOriginalPrice!, displayPrice) : 0;
  const translatedDuration = translateDuration(pkg.duration, t);
  const perWeek = pkg.durationWeeks && pkg.durationWeeks > 0
    ? formatPrice(Math.round(displayPrice / pkg.durationWeeks), displayCurrency, lang)
    : null;
  const showOriginalCurrency = pkg.priceEur && pkg.currency !== 'EUR';

  // Show first 2 included items as preview
  const includedPreview = pkg.includedItems?.slice(0, 2) || [];

  return (
    <article
      className={`program-card program-card--pkg ${hasPromo ? 'has-promotion' : ''}`}
      style={{ animationDelay: `${index * 0.05}s` }}
      onClick={onSelect}
    >
      {/* Logo spanning full height */}
      <div className="program-logo">
        {pkg.photoUrl && !logoError ? (
          <img
            src={pkg.photoUrl}
            alt={pkg.schoolName}
            onError={() => setLogoError(true)}
          />
        ) : (
          <div className="program-logo-placeholder program-logo-placeholder--pkg">
            <PackageIcon />
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="program-main">
        {/* Zone 1: Type & Offers */}
        <div className="program-zone-type">
          <span className="category-badge category-badge--green">
            <PackageIcon />
            <span>{t('programCard.allInclusive')}</span>
          </span>
          {hasPromo && (
            <span className="promo-badge">
              <SparkleIcon />
              <span>-{discount}%</span>
            </span>
          )}
        </div>

        {/* Zone 2: Identity */}
        <div className="program-zone-identity">
          <h3 className="program-name">{pkg.name}</h3>
          <span className="program-school">{pkg.schoolName}</span>
        </div>

        {/* Zone 3: Quick Specs Grid */}
        <div className="program-zone-specs">
          <div className="spec-item">
            <MapPinIcon />
            <span>{flag} {getCity(pkg.location)}</span>
          </div>
          <div className="spec-item">
            <DurationIcon />
            <span>{translatedDuration}</span>
          </div>
          {startDate && (
            <div className="spec-item">
              <CalendarIcon />
              <span>{startDate}</span>
            </div>
          )}
          {pkg.ageRequirement && (
            <div className="spec-item">
              <UserIcon />
              <span>{pkg.ageRequirement}</span>
            </div>
          )}
          {pkg.requiredEnglishLevel && (
            <div className="spec-item">
              <LevelIcon />
              <span>{t('programCard.minLevel')}: {translateLevel(pkg.requiredEnglishLevel, t)}</span>
            </div>
          )}
        </div>

        {/* Included items preview */}
        {includedPreview.length > 0 && (
          <div className="program-zone-included">
            {includedPreview.map((item, i) => (
              <span key={i} className="included-item">
                <CheckIcon />
                <span>{item}</span>
              </span>
            ))}
            {pkg.includedItems && pkg.includedItems.length > 2 && (
              <span className="included-more">+{pkg.includedItems.length - 2}</span>
            )}
          </div>
        )}

        {/* Zone 4: Price */}
        <div className="program-zone-price">
          <div className="program-prices">
            {hasPromo && (
              <span className="program-price-old">
                {formatPrice(displayOriginalPrice!, displayCurrency, lang)}
              </span>
            )}
            <span className={`program-price program-price--pkg ${hasPromo ? 'program-price--promo' : ''}`}>
              {formatPrice(displayPrice, displayCurrency, lang)}<span className="price-asterisk">*</span>
            </span>
          </div>
          {perWeek && (
            <span className="program-price-weekly">{perWeek}/{t('programCard.perWeek')}</span>
          )}
          {showOriginalCurrency && (
            <span className="program-price-original">
              (~{formatPrice(pkg.price, pkg.currency, lang)})
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

// ─── Detail Modal ──────────────────────────────────────────────

interface ProgramDetailModalProps {
  program: ProgramData;
  onClose: () => void;
}

const ProgramDetailModal: React.FC<ProgramDetailModalProps> = ({ program, onClose }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const course = isCourse(program);
  const pkg = isPackage(program);
  // Use priceEur if available, otherwise fallback to price (for mock data compatibility)
  const displayPrice = program.priceEur ?? program.price;
  const displayCurrency = program.priceEur ? 'EUR' : program.currency;
  const displayOriginalPrice = program.originalPriceEur ?? program.originalPrice;
  const perWeek =
    program.durationWeeks && program.durationWeeks > 0
      ? formatPrice(Math.round(displayPrice / program.durationWeeks), displayCurrency, lang)
      : null;
  const hasPromo = program.hasPromotion && displayOriginalPrice;
  const discount = hasPromo ? calculateDiscount(displayOriginalPrice!, displayPrice) : 0;
  const showOriginalCurrency = program.priceEur && program.currency !== 'EUR';
  const flag = getCountryFlag(program.location);
  const categoryStyle = course ? getCategoryStyle(program.courseCategory) : null;
  const categoryLabel = course ? translateCategory(program.courseCategory, t, t('programCard.fallbackCategory')) : null;
  const translatedDuration = translateDuration(program.duration, t);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon />
        </button>

        {/* Modal header with badges */}
        <div className="modal-header">
          {course && categoryStyle && (
            <span className={`category-badge category-badge--${categoryStyle.color}`}>
              <categoryStyle.icon />
              <span>{categoryLabel}</span>
            </span>
          )}
          {pkg && (
            <span className="category-badge category-badge--green">
              <PackageIcon />
              <span>{t('programCard.allInclusive')}</span>
            </span>
          )}
          {hasPromo && <span className="modal-discount">-{discount}%</span>}
        </div>

        <div className="modal-body">
          <h2 className="modal-name">{program.name}</h2>
          <p className="modal-school">{program.schoolName}</p>

          <div className="modal-details">
            <div className="modal-detail-row">
              <span className="modal-detail-label">{t('programCard.modal.location')}</span>
              <span className="modal-detail-value">
                {flag && <span className="program-flag">{flag}</span>} {program.location}
              </span>
            </div>
            <div className="modal-detail-row">
              <span className="modal-detail-label">{t('programCard.modal.duration')}</span>
              <span className="modal-detail-value">{translatedDuration}</span>
            </div>
            {program.startDate && (
              <div className="modal-detail-row">
                <span className="modal-detail-label">{t('programCard.modal.startDate')}</span>
                <span className="modal-detail-value">{program.startDate}</span>
              </div>
            )}
            {program.ageRequirement && (
              <div className="modal-detail-row">
                <span className="modal-detail-label">{t('programCard.modal.age')}</span>
                <span className="modal-detail-value">{program.ageRequirement}</span>
              </div>
            )}
            {course && program.hoursPerWeek && (
              <div className="modal-detail-row">
                <span className="modal-detail-label">{t('programCard.modal.intensity')}</span>
                <span className="modal-detail-value">{Math.round(program.hoursPerWeek)} {t('programCard.hoursPerWeek')}</span>
              </div>
            )}
            {program.requiredEnglishLevel && (
              <div className="modal-detail-row">
                <span className="modal-detail-label">{t('programCard.modal.minLevel')}</span>
                <span className="modal-detail-value">{translateLevel(program.requiredEnglishLevel, t)}</span>
              </div>
            )}
          </div>

          {program.description && (
            <div className="modal-description">
              <span className="modal-description-title">{t('programCard.modal.description')}</span>
              <div
                className="modal-description-text"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(program.description) }}
              />
            </div>
          )}

          {pkg && program.includedItems && program.includedItems.length > 0 && (
            <div className="modal-included">
              <span className="modal-included-title">{t('programCard.includes')}</span>
              <ul className="modal-included-list">
                {program.includedItems.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="modal-price-section">
            <div className="modal-price-main">
              {hasPromo && (
                <span className="modal-price-old">{formatPrice(displayOriginalPrice!, displayCurrency, lang)}</span>
              )}
              <span className={`modal-price ${hasPromo ? 'modal-price--promo' : ''}`}>
                {formatPrice(displayPrice, displayCurrency, lang)}
              </span>
            </div>
            {perWeek && <span className="modal-price-weekly">{perWeek} / {t('programCard.perWeek')}</span>}
            {showOriginalCurrency && (
              <span className="modal-price-original">
                (~{formatPrice(program.price, program.currency, lang)})
              </span>
            )}
          </div>

          <div className="modal-disclaimer">{t('programCard.priceDisclaimer')}</div>
        </div>
      </div>
    </div>
  );
};
